import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import type { AdminSession } from '../types'
import { useI18n } from '../i18n'

type Props = {
  open: boolean
  session: AdminSession | null
  onTrust: () => void
  onLogout: () => void
  trusting: boolean
}

const formatLocation = (session: AdminSession | null) => {
  const loc = session?.location
  if (!loc) return null
  const parts = [loc.city, loc.region, loc.country].filter(Boolean)
  return parts.join(', ')
}

function UntrustedDeviceDialog({ open, session, onTrust, onLogout, trusting }: Props) {
  const { t } = useI18n()
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => null}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white p-6 text-left align-middle shadow-2xl shadow-slate-900/20 transition-all">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Dialog.Title className="text-xl font-semibold text-slate-900">
                      {t('untrusted.title')}
                    </Dialog.Title>
                    <Dialog.Description className="mt-1 text-sm text-slate-600">
                      {t('untrusted.description')}
                    </Dialog.Description>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100 text-amber-700">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      className="h-6 w-6"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m0 3.75h.008v.008H12v-.008Z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />
                    </svg>
                  </div>
                </div>

                <div className="mt-4 space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">{t('untrusted.device')}</span>
                    <span className="font-semibold text-slate-900">
                      {session?.deviceName ?? t('untrusted.unknown')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-600">{t('untrusted.ip')}</span>
                    <span className="font-semibold text-slate-900">
                      {session?.ip ?? t('untrusted.unknown')}
                    </span>
                  </div>
                  {formatLocation(session) && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">{t('untrusted.location')}</span>
                      <span className="font-semibold text-slate-900">{formatLocation(session)}</span>
                    </div>
                  )}
                  {session?.userAgent && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">{t('untrusted.userAgent')}</span>
                      <span className="max-w-[260px] text-right text-xs text-slate-500">
                        {session.userAgent}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex items-center justify-between text-sm font-semibold">
                  <button
                    type="button"
                    onClick={onLogout}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-slate-700 transition hover:border-slate-300"
                  >
                    {t('untrusted.logout')}
                  </button>
                  <button
                    type="button"
                    onClick={onTrust}
                    disabled={trusting}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-white shadow-lg shadow-emerald-200 transition hover:-translate-y-0.5 hover:bg-emerald-600 disabled:translate-y-0 disabled:opacity-60"
                  >
                    {trusting ? t('untrusted.trusting') : t('untrusted.trust')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default UntrustedDeviceDialog
